import React, { useEffect, useState } from "react";
import RulesListDataTable from "../DataGrids/RulesListDataGrid";
import { useThemeMode } from "../../contexts/ThemeModeContext";
import { darkModeTokens } from "../../styles/Theme";
import {
  calculateDynamicWidth,
  getSanatizedCode,
  getFieldToPrefixMappingForSnapshot,
  transformHeaderName,
  RecordType,
  createColumnGroupingModelForSnapshot,
  deduplicateByFieldProperty,
  excludeInternalResultColumns,
} from "../../services/utils";

import { Box, Fade, IconButton } from "@mui/material";
import { GRID_CHECKBOX_SELECTION_COL_DEF } from "@mui/x-data-grid-pro";
import Loader from "../Molecules/Loader/Loader";
import useFetchMismatchRecordsWithSnapshotByExecutionId from "../../hooks/useFetchMismatchRecordsWithSnapshotByExecutionId";
import { isEmpty } from "lodash";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import { getConfig } from "../../config/runtimeConfig";

interface MismatchedRecordsDashboardTabProps {
  dashboardData: any;
  setMismatchedColumns: any;
  userDialogData?: any;
  setUserDialogData: any;
}

const MismatchedSnapshotRecordsDashboardTab = ({
  dashboardData,
  setMismatchedColumns,
}: MismatchedRecordsDashboardTabProps) => {
  const { rowsPerPageOptions } = useRowsPerPage();
  const { mode } = useThemeMode();
  const isDark = mode === "dark";
  const [mismatchedRowsData, setMismatchedRowsData] = useState<any>([]);
  const [mismatchedColumnsData, setMismatchedColumnsData] = useState<any>([]);
  const [columnGroupingModel, setColumnGroupingModel] = useState<any>([]);

  const [isMisMatchLoading, setIsMisMatchLoading] = useState<boolean>(false);
  //for pagination default values and states
  const defaultPage = getConfig("REACT_APP_DEFAULT_PAGE", "1");
  const defaultPageSize = getConfig("REACT_APP_DEFAULT_PAGE_SIZE", "25");
  const [page, setPage] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1,
  );
  const [pSize, setPSize] = useState<number>(
    defaultPageSize ? parseInt(defaultPageSize) : 25,
  );
  const [selectedIds, setSelectedIds] = useState<any>([]);
  const [keyColumns, setKeyColumns] = useState<any>([]);
  const [isAllColumnPinned, setIsAllColumnPinned] = useState(false);

  const [mismatchResults] = useFetchMismatchRecordsWithSnapshotByExecutionId({
    setIsLoading: setIsMisMatchLoading,
    isMismatchFetch: true,
    currentRuleResultId: dashboardData?.id,
    page,
    pSize,
  });
  useEffect(() => {
    if (mismatchResults?.items && mismatchResults?.items.length > 0) {
      const keyColumns = Object.keys(
        mismatchResults?.items[0]?.comparison_keys,
      ).map((key, value) => key);

      setKeyColumns(keyColumns);
    }
  }, [mismatchResults]);
  useEffect(() => {
    if (!mismatchResults || !dashboardData) {
      return;
    }

    const columnGrouping = createColumnGroupingModelForSnapshot(
      dashboardData,
      mismatchResults.items,
      "mismatched" as RecordType,
      isAllColumnPinned,
      "Mismatched Values",
    );

    setColumnGroupingModel(columnGrouping);
  }, [dashboardData, mismatchResults, isAllColumnPinned]);

  const processRows = (rows: any[]) => {
    if (!rows) return;
    return rows.map((row: any) => {
      const updatedRow = { ...row };

      Object.keys(updatedRow).forEach((key) => {
        if (typeof updatedRow[key] === "string") {
          if (
            /\.0$/.test(updatedRow[key]) &&
            !/\.0[1-9]/.test(updatedRow[key])
          ) {
            updatedRow[key] = updatedRow[key].slice(0, -2);
          }
        }
      });

      return updatedRow;
    });
  };

  const getColumns = (comparison_rule_exec_details: any) => {
    let finalColumns;
    const firstColumns: any = Object.keys(
      comparison_rule_exec_details[0]?.comparison_keys,
    );
    const recordData =
      comparison_rule_exec_details[0]?.record_snapshot?.record_data;
    const fieldToPrefixMap = getFieldToPrefixMappingForSnapshot(
      comparison_rule_exec_details,
      dashboardData,
      "mismatched",
    );

    const fileColumns: any = Object.keys(
      comparison_rule_exec_details[0]?.file_names,
    );
    const column_values: any = Object.keys(
      comparison_rule_exec_details[0]?.column_values,
    );
    const columnFirst: any = [...firstColumns].map((item: any) => {
      return {
        field: item,
        headerName: getSanatizedCode(item),
        flex: 1,
        minWidth: 110,
        renderCell: (params: any) => {
          return <div className="long-string-width">{params.value}</div>;
        },
      };
    });
    const column_values_data: any = [...column_values].map((item: any) => {
      return {
        field: item,
        headerName: `${item} values`,
        flex: 1,
        minWidth: 110,
        renderCell: (params: any) => {
          return <div className="long-string-width">{params.value}</div>;
        },
      };
    });
    const columnFile: any = [...fileColumns].map((item: any) => {
      const dynamicWidth = calculateDynamicWidth(
        item,
        comparison_rule_exec_details,
        "missmatched",
      );
      const transformedHeaderName = transformHeaderName(
        item,
        item,
        fieldToPrefixMap,
      );

      return {
        field: item,
        headerName: getSanatizedCode(transformedHeaderName),
        flex: 1,
        minWidth: dynamicWidth > 120 ? dynamicWidth : 120,
        renderCell: (params: any) => (
          <div
            className="data-grid-cell word-break-all"
            dangerouslySetInnerHTML={{ __html: params.value }}
          />
        ),
      };
    });
    // Initialize variables for snapshot columns
    let filtredSnapShot: any[] = [];
    if (recordData) {
      const snapshotColumns = recordData
        ? Object.keys(recordData).map((key) => {
            const dynamicWidth = calculateDynamicWidth(
              key,
              comparison_rule_exec_details,
              "missmatched",
            );
            const transformedHeaderName = transformHeaderName(
              key,
              key,
              fieldToPrefixMap,
            );
            return {
              field: key,
              headerName: transformedHeaderName,
              flex: 1,
              minWidth: dynamicWidth > 120 ? dynamicWidth : 120,
            };
          })
        : [];

      const comparisonKeys = [
        ...columnFirst,
        ...column_values_data,
        ...columnFile,
      ];
      filtredSnapShot = snapshotColumns.filter(
        (column) =>
          !comparisonKeys.some(
            (comparisonColumn) => comparisonColumn.field === column.field,
          ),
      );
    }
    // Enhanced grouping function to create distinct sections aligned with column grouping model
    const groupColumnsByPrefix = (columns: any[]) => {
      const prefixes =
        dashboardData?.additional_properties?.resource_prefixes || [];
      const resourceColumns: any[] = [];
      const prefixColumns: any[] = [];
      const commonColumns: any[] = [];

      // 1. First, collect base resource columns (exact matches with prefixes)
      prefixes.forEach((prefix: string) => {
        const baseColumn = columns.find((col) => col.field === prefix);
        if (baseColumn) {
          resourceColumns.push(baseColumn);
        }
      });

      // 2. Then, collect prefix-specific columns in organized order
      prefixes.forEach((prefix: string) => {
        // File name column (e.g., "Factset_file_name")
        const fileColumn = columns.find(
          (col) => col.field === `${prefix}_file_name`,
        );
        if (fileColumn) {
          prefixColumns.push(fileColumn);
        }

        // Other prefix-specific columns (excluding base, file_name, and Mismatched_)
        const otherPrefixColumns = columns.filter(
          (col) =>
            col.field.startsWith(`${prefix}_`) &&
            !col.field.endsWith("_file_name") &&
            !col.field.startsWith(`Mismatched_${prefix}_`),
        );
        prefixColumns.push(...otherPrefixColumns);

        // Mismatched value column (e.g., "Mismatched_Factset_Value")
        const mismatchedColumn = columns.find(
          (col) => col.field === `Mismatched_${prefix}_Value`,
        );
        if (mismatchedColumn) {
          prefixColumns.push(mismatchedColumn);
        }
      });

      // 3. Finally, collect columns that don't match any prefix pattern (common columns)
      columns.forEach((col) => {
        const isGrouped = prefixes.some(
          (prefix: string) =>
            col.field === prefix ||
            col.field.startsWith(`${prefix}_`) ||
            col.field === `Mismatched_${prefix}_Value`,
        );
        if (!isGrouped) {
          commonColumns.push(col);
        }
      });

      // Return in the order: Resources, Prefix-specific, Common
      return [...resourceColumns, ...commonColumns, ...prefixColumns];
    };

    // Determine which columns to group based on recordData availability
    const dataColumns = recordData
      ? [...column_values_data, ...columnFile, ...filtredSnapShot]
      : [...column_values_data, ...columnFile];

    const groupedDataColumns = groupColumnsByPrefix(dataColumns);

    finalColumns = [...columnFirst, ...groupedDataColumns];

    // Deduplicate columns based on the "field" property, keeping the first occurrence
    const deduplicatedColumns = deduplicateByFieldProperty(
      finalColumns,
      "field",
    );

    setMismatchedColumnsData(excludeInternalResultColumns(deduplicatedColumns));
  };
  const reverseFormattedKeysPairs = (inputObject: any) => {
    const desiredOutput = Object.entries(inputObject)
      .filter(([key]) => key !== "id")
      .map(([key, value]) => `${key}=${value}`)
      .join(", ");
    return desiredOutput;
  };

  const handleRowData = (comparison_rule_exec_details: any) => {
    const rows: any = [];
    const checkNames: any = [];
    const recordData =
      comparison_rule_exec_details[0]?.record_snapshot?.record_data;

    comparison_rule_exec_details?.forEach((item: any) => {
      checkNames.push(item?.check_name);
      if (item) {
        const fileNames = Object.entries(item?.file_names).reduce(
          (acc: any, [key, value]: any) => {
            acc[key] = getSanatizedCode(value, ";");
            return acc;
          },
          {},
        );
        const row: any = {
          id: item?.id,
          rowId: item?.id,
          assigned_user: item?.assigned_user,
          current_status: item?.current_status,
          ...(recordData
            ? {}
            : {
                ...item?.comparison_keys,
                ...fileNames,
              }),
        };
        rows.push(row);
      }
    });
    let updatedRows: any = [];
    let newRow: any = {};

    comparison_rule_exec_details?.forEach((item: any) => {
      if (item) {
        const row = rows.find((row: any) => item?.id === row?.rowId);
        if (recordData) {
          newRow = {
            ...row,
            rowId: item?.id,
            assigned_user: item?.assigned_user,
            comment: item?.comment,
            current_status: item?.current_status,
            ...item?.column_values,
            ...item?.record_snapshot?.record_data,
          };
        } else {
          newRow = {
            ...row,
            rowId: item?.id,
            assigned_user: item?.assigned_user,
            comment: item?.comment,
            current_status: item?.current_status,
            ...item?.column_values,
          };
        }
        updatedRows.push(newRow);
      }
    });
    const commonKeys =
      updatedRows?.length > 0 &&
      Object.keys(updatedRows[0]).filter(
        (key) => !checkNames.includes(key) && key !== "id",
      );

    function mergeObjectsByKey(arr: any, keys: any) {
      const mergedMap = new Map();

      arr.forEach((obj: any) => {
        const key = keys.map((k: any) => obj[k]).join("-");
        if (mergedMap.has(key)) {
          Object.assign(mergedMap.get(key), obj);
        } else {
          mergedMap.set(key, { ...obj });
        }
      });

      return Array.from(mergedMap.values());
    }

    const mergedData = mergeObjectsByKey(updatedRows, commonKeys);
    const processedRows: any = processRows(mergedData);
    setMismatchedRowsData(processedRows);
  };

  useEffect(() => {
    // mismatched records
    if (mismatchResults?.items?.length > 0) {
      getColumns(mismatchResults?.items);
      handleRowData(mismatchResults?.items);
    }
  }, [mismatchResults]);

  const [pinnedColumns, setPinnedColumns] = useState({
    left: [GRID_CHECKBOX_SELECTION_COL_DEF.field],
    right: ["current_status", "username", "comment", "action"],
  });

  useEffect(() => {
    setPinnedColumns((prevState) => {
      const updatedLeft = [
        GRID_CHECKBOX_SELECTION_COL_DEF.field,
        ...keyColumns,
      ];
      const updatedRight = [...prevState.right];

      return {
        left: updatedLeft,
        right: updatedRight,
      };
    });
  }, [keyColumns]);

  const handleMoveColumns = () => {
    setPinnedColumns((prevState) => {
      const columnsToAdd = ["current_status", "username", "comment"];
      const isColumnsAvailable = columnsToAdd.every((item) =>
        prevState.right.includes(item),
      );
      const isKeyColumnAvailable = keyColumns.every((item: any) =>
        prevState.left.includes(item),
      );
      setIsAllColumnPinned(isColumnsAvailable);
      if (isColumnsAvailable && isKeyColumnAvailable) {
        return {
          left: prevState.left.filter((item) => !keyColumns.includes(item)),
          right: prevState.right.filter((item) => !columnsToAdd.includes(item)),
        };
      } else {
        return {
          left: [...prevState.left, ...keyColumns],
          right: [...columnsToAdd, ...prevState.right],
        };
      }
    });
  };

  /**
   * Determines if checkbox column should be visible
   *
   * Uses lodash isEmpty() to check if comparison_research_query_results is empty.
   * lodash isEmpty() returns true for: null, undefined, {}, [], "", Map(), Set()
   *
   * Checkbox is VISIBLE when:
   * - missingRowsData has at least one row (length > 0), AND
   * - At least ONE of the following conditions is true:
   *   1. is_issues_data_available === true, OR
   *   2. comparison_research_query_results is a non-empty array or non-empty object
   *
   * Checkbox is HIDDEN when:
   * - missingRowsData has no rows (length === 0), OR
   * - missingRowsData has rows BUT BOTH of the following are true:
   *   1. is_issues_data_available is false/null/undefined, AND
   *   2. comparison_research_query_results is null/undefined/empty array/empty object
   */
  const isCheckboxRequired =
    // First condition: Must have data rows
    Array.isArray(mismatchedRowsData) &&
    mismatchedRowsData.length > 0 &&
    // Second condition: At least one of these must be true
    // Option 1: Issues data is available
    (dashboardData?.is_issues_data_available === true ||
      // Option 2: Comparison research query results exist and are not empty (using lodash isEmpty)
      !isEmpty(dashboardData?.comparison_research_query_results));

  return (
    <>
      <Box className="position-relative">
        {isMisMatchLoading && (
          <Box className="adhock-query-accordion-wrapper">
            <Loader
              isAbsolute={true}
              radius={{ top: 0, right: 0, bottom: 5, left: 5 }}
              isLoading={isMisMatchLoading}
            />
          </Box>
        )}
        {keyColumns && (
          <>
            <Box className="position-relative">
              {mismatchedRowsData && mismatchedRowsData?.length !== 0 ? (
                <Fade in timeout={300}>
                  <Box>
                    <RulesListDataTable
                      selectedIds={selectedIds}
                      setSelectedIds={setSelectedIds}
                      dataRows={mismatchedRowsData || []}
                      dataColumns={mismatchedColumnsData || []}
                      loading={isMisMatchLoading}
                      className="dataTable datagridHeaderColumn no-radius pt-0 bdr-top-0 datatable-column-sep checkbox-result-column pinnedColumnHeaders-bg-height hide-progress-icon"
                      disableColumnFilter={false}
                      tableHeight={180}
                      setResultColumns={setMismatchedColumns}
                      checkboxSelection={isCheckboxRequired}
                      disableColumnReorder={true}
                      columnGroupingModel={columnGroupingModel}
                      paginationMode="server"
                      rowCount={mismatchResults?.total || 0}
                      pageSizeOptions={rowsPerPageOptions}
                      paginationModel={{
                        page: page - 1,
                        pageSize: pSize,
                      }}
                      onPaginationModelChange={(params: any) => {
                        if (
                          params.pageSize !== pSize ||
                          params.page !== page - 1
                        ) {
                          setPage(params.page + 1);
                          setPSize(params.pageSize);
                        }
                      }}
                      singlePageMaxHeightDiff={262}
                      pinnedColumns={
                        mismatchedRowsData?.length > 0 ? pinnedColumns : {}
                      }
                      disableColumnPinning={false}
                      variant="premium"
                      resourcePrefixes={
                        dashboardData?.additional_properties?.resource_prefixes
                      }
                    />
                  </Box>
                </Fade>
              ) : (
                <Box
                  className="adhock-query-accordion-wrapper"
                  sx={{
                    paddingTop: "24px",
                    paddingBottom: "24px",
                    display: "flex",
                    justifyContent: "center",
                    borderTop: "none",
                    backgroundColor: isDark
                      ? darkModeTokens.surfaceInset
                      : "transparent",
                    color: "text.secondary",
                  }}
                >
                  No data available
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
    </>
  );
};

export default MismatchedSnapshotRecordsDashboardTab;
